import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { api, ApiError, type MachineryInput } from '../api/client'
import { invalidateMachinery } from '../api/invalidate'
import { requireAccessToken } from '../auth/msal'
import './MachineryDetail.css'

/**
 * One machine.
 *
 * No compliance panel and no obligations, because there are none: the asset list
 * carries no dates for plant. What it does carry that is worth a screen of its own
 * is the identity — the asset code and serial the client files it under — and the
 * cross-link to the vehicle register where the machine also has a plate.
 */
export function MachineryDetail() {
  const { machineryId } = useParams<{ machineryId: string }>()
  const id = Number(machineryId)
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  // ?edit=1 so the pencil in a register row opens straight into the form rather
  // than landing on the record and asking for a second click.
  const [params] = useSearchParams()
  const [editing, setEditing] = useState(params.get('edit') === '1')
  const [showHistory, setShowHistory] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const { data, isPending } = useQuery({
    queryKey: ['machine', id],
    queryFn: () => api.getMachine(id),
    enabled: Number.isFinite(id),
  })

  /*
      Fetched only when it is asked for.

      MachineryEditService has been writing these entries since day one and
      nothing read them back, which is the same as not having an audit trail. The
      vehicle page puts its history behind a tab for the same reason: most visits
      want the record, not its past.
  */
  const history = useQuery({
    queryKey: ['machine-activity', id],
    queryFn: () => api.getMachineActivity(id),
    enabled: showHistory && Number.isFinite(id),
  })

  const save = useMutation({
    mutationFn: async (input: MachineryInput) => {
      // Called at the top of the click, while the gesture's call stack is still
      // live. A browser withdraws user activation once that unwinds, so a token
      // popup opened after an await is blocked every time.
      await requireAccessToken()
      return api.updateMachine(id, input, data!.rowVersion!)
    },
    onSuccess: async () => {
      await Promise.all([
        invalidateMachinery(queryClient),
        queryClient.invalidateQueries({ queryKey: ['machine', id] }),
      ])
      setEditing(false)
      setError(null)
    },
    onError: (e: Error) => setError(e.message),
  })

  const remove = useMutation({
    mutationFn: async () => {
      await requireAccessToken()

      // Re-read before deleting rather than sending the version this page loaded
      // with. Anything that has written to the row since — an import, a
      // colleague's edit — moves it, and the delete then comes back 409 under a
      // message about somebody else when nobody else was involved.
      const fresh = await api.getMachine(id)
      return api.deleteMachine(id, fresh.rowVersion!)
    },
    onSuccess: async () => {
      await invalidateMachinery(queryClient)
      navigate('/machinery', { replace: true })
    },
    onError: (e: Error) =>
      setError(
        e instanceof ApiError && e.isConflict
          ? 'The machine was changed or already removed. Reload and try again.'
          : e.message,
      ),
  })

  if (isPending) return <div className="mdet"><p className="mdet__loading">Loading…</p></div>

  if (!data) {
    return (
      <div className="mdet">
        <p className="mdet__loading">That machine is not on the register.</p>
        <Link to="/machinery">← Machinery</Link>
      </div>
    )
  }

  const title = [data.categoryName, [data.make, data.model].filter(Boolean).join(' ')]
    .filter(Boolean)
    .join(' · ')

  return (
    <div className="mdet">
      <button type="button" className="mdet__back" onClick={() => navigate('/machinery')}>
        ← Machinery
      </button>

      <header className="mdet__head">
        <div>
          <h1>{data.assetCode ?? data.serialNumber ?? `Machine ${data.machineryId}`}</h1>
          <p className="mdet__sub">{title}</p>
        </div>

        <div className="mdet__actions">
          <button
            type="button"
            className="btn"
            aria-expanded={showHistory}
            onClick={() => setShowHistory((v) => !v)}
          >
            {showHistory ? 'Hide history' : 'History'}
          </button>
          <button type="button" className="btn" onClick={() => setEditing((v) => !v)}>
            {editing ? 'Cancel' : 'Edit'}
          </button>
          <button
            type="button"
            className="btn btn--danger"
            disabled={remove.isPending}
            onClick={() => remove.mutate()}
          >
            {remove.isPending ? 'Removing…' : 'Remove'}
          </button>
        </div>
      </header>

      {error && (
        <p className="mdet__error" role="alert">
          {error}
        </p>
      )}

      {/*
        The cross-link, and the whole reason the plate is stored on a machinery
        row at all. Resolved live rather than copied at import, so it appears the
        moment a matching vehicle is added and goes when that vehicle is removed.
      */}
      {data.registrationNumberCanonical && data.vehicleId && (
        <div className="mdet__crosslink">
          <p>
            <b>{data.registrationNumberCanonical}</b> is also on the vehicle register
            {data.vehicleTypeName ? ` as a ${data.vehicleTypeName}` : ''}. Its insurance,
            fitness and permit are tracked there, not here.
          </p>
          <Link className="btn" to={`/vehicles/${data.registrationNumberCanonical}`}>
            Open {data.registrationNumberCanonical} →
          </Link>
        </div>
      )}

      {editing ? (
        <MachineForm
          initial={data}
          busy={save.isPending}
          onSubmit={(input) => save.mutate(input)}
        />
      ) : (
        <div className="mdet__panels">
          <Panel title="Identity">
            <Fact label="Asset code" value={data.assetCode} mono />
            <Fact label="Serial number" value={data.serialNumber} mono />
            <Fact label="Registration" value={data.registrationNumberCanonical} mono />
            <Fact label="Category" value={data.categoryName} />
          </Panel>

          <Panel title="Made">
            <Fact label="Make" value={data.make} />
            <Fact label="Model" value={data.model} />
            <Fact
              label="Year"
              value={data.manufactureYear ? String(data.manufactureYear) : null}
            />
          </Panel>

          <Panel title="Where">
            <Fact label="Site" value={data.siteNameRaw} />
            {/*
              The sheet's second site column has no header and disagrees with the
              first on 151 rows. Shown only when it differs, and labelled for what
              it is — which of the two is authoritative is the client's call.
            */}
            {data.altSiteName && data.altSiteName !== data.siteNameRaw && (
              <Fact
                label="Second site column"
                value={data.altSiteName}
                note="unlabelled in the asset list, and it disagrees with the first"
              />
            )}
            <Fact
              label="Sheet row"
              value={data.sourceSerialNo ? `#${data.sourceSerialNo}` : null}
            />
          </Panel>

          <Panel title="Remarks">
            <Fact label="Remark" value={data.remarks} />
            <Fact label="Remark (second column)" value={data.remarksAlt} />
          </Panel>
        </div>
      )}

      {showHistory && (
        <section className="mdet__history">
          <h2>History</h2>

          {history.isPending && <p className="mdet__loading">Loading history…</p>}

          {history.data?.items.length === 0 && (
            <p className="mdet__loading">
              Nothing has been recorded against this machine yet. An import writes one
              entry per batch rather than one per machine, so a machine that arrived
              with the asset list and has not been touched since has no history of its
              own.
            </p>
          )}

          {history.data?.items.map((entry) => (
            <article key={entry.activityLogId} className="mdet__event">
              <div className="mdet__eventtop">
                <span className="mdet__action">{entry.action}</span>
                <time className="mono">{new Date(entry.performedAtUtc!).toLocaleString('en-IN')}</time>
              </div>

              <p className="mdet__what">
                {entry.fieldName && <strong>{entry.fieldName}: </strong>}
                {entry.oldValue || entry.newValue ? (
                  <>
                    changed from <span className="mono">{entry.oldValue || '—'}</span> to{' '}
                    <span className="mono">{entry.newValue || '—'}</span>
                  </>
                ) : (
                  (entry.notes ?? '—')
                )}
              </p>

              <div className="mdet__eventmeta mono">
                {entry.performedByName ?? entry.performedBy}
              </div>
            </article>
          ))}
        </section>
      )}
    </div>
  )
}

function Panel({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mdet__panel">
      <h2>{title}</h2>
      <dl>{children}</dl>
    </section>
  )
}

function Fact({
  label,
  value,
  mono,
  note,
}: {
  label: string
  value: string | null | undefined
  mono?: boolean
  note?: string
}) {
  return (
    <>
      <dt>{label}</dt>
      <dd className={mono ? 'mono' : undefined}>
        {/* An em dash, never a zero or an "N/A". A blank is a fact about this
            register, and filling one in would be inventing data. */}
        {value ? value : <span className="mdet__blank">—</span>}
        {note && <span className="mdet__note">{note}</span>}
      </dd>
    </>
  )
}

function MachineForm({
  initial,
  busy,
  onSubmit,
}: {
  initial: MachineryInput & { categoryName: string }
  busy: boolean
  onSubmit: (input: MachineryInput) => void
}) {
  const [form, setForm] = useState<MachineryInput>({
    categoryName: initial.categoryName,
    assetCode: initial.assetCode ?? '',
    serialNumber: initial.serialNumber ?? '',
    registrationNumberCanonical: initial.registrationNumberCanonical ?? '',
    make: initial.make ?? '',
    model: initial.model ?? '',
    manufactureYear: initial.manufactureYear ?? null,
    siteNameRaw: initial.siteNameRaw ?? '',
    altSiteName: initial.altSiteName ?? '',
    remarks: initial.remarks ?? '',
    remarksAlt: initial.remarksAlt ?? '',
  })

  function set<K extends keyof MachineryInput>(key: K, value: MachineryInput[K]) {
    setForm((f) => ({ ...f, [key]: value }))
  }

  const identityChanged =
    (form.assetCode ?? '') !== (initial.assetCode ?? '') ||
    (form.serialNumber ?? '') !== (initial.serialNumber ?? '')

  return (
    <form
      className="mdet__form"
      onSubmit={(e) => {
        e.preventDefault()
        // Empty string means CLEARED here, not "leave it". That is the opposite
        // of the import path, and it is why usp_Machinery_UpdateDetails exists
        // separately from the upsert.
        const cleaned = Object.fromEntries(
          Object.entries(form).map(([k, v]) => [k, v === '' ? null : v]),
        ) as unknown as MachineryInput

        onSubmit({ ...cleaned, categoryName: form.categoryName })
      }}
    >
      {/*
        ⚠️ These two ARE the machine's identity, and saying so on the form is not
        decoration. A re-import matches on the asset code and serial together, so
        changing either one means the next import does not recognise this machine
        and adds a second copy of it — while the hand-edited row it could not find
        is left stranded. Found by clearing them during testing and watching the
        register go from 562 to 563.
      */}
      {identityChanged && (
        <p className="mdet__formwarn" role="status">
          The asset code and serial together are how a re-import recognises this
          machine. Changing either means the next import will not find it and will
          add a second copy instead.
        </p>
      )}

      <Field label="Category" required value={form.categoryName} onChange={(v) => set('categoryName', v)} />
      <Field label="Asset code" value={form.assetCode ?? ''} onChange={(v) => set('assetCode', v)} />
      <Field label="Serial number" value={form.serialNumber ?? ''} onChange={(v) => set('serialNumber', v)} />
      <Field
        label="Registration"
        value={form.registrationNumberCanonical ?? ''}
        onChange={(v) => set('registrationNumberCanonical', v)}
        hint="Only if this machine carries an RTO plate. Links it to the vehicle register."
      />
      <Field label="Make" value={form.make ?? ''} onChange={(v) => set('make', v)} />
      <Field label="Model" value={form.model ?? ''} onChange={(v) => set('model', v)} />
      <Field
        label="Year"
        type="number"
        value={form.manufactureYear ? String(form.manufactureYear) : ''}
        onChange={(v) => set('manufactureYear', v ? Number(v) : null)}
      />
      <Field label="Site" value={form.siteNameRaw ?? ''} onChange={(v) => set('siteNameRaw', v)} />
      <Field label="Second site column" value={form.altSiteName ?? ''} onChange={(v) => set('altSiteName', v)} />
      <Field label="Remark" value={form.remarks ?? ''} onChange={(v) => set('remarks', v)} />
      <Field label="Remark (second column)" value={form.remarksAlt ?? ''} onChange={(v) => set('remarksAlt', v)} />

      <div className="mdet__formactions">
        <button type="submit" className="btn btn--primary" disabled={busy || !form.categoryName.trim()}>
          {busy ? 'Saving…' : 'Save'}
        </button>
      </div>
    </form>
  )
}

function Field({
  label,
  value,
  onChange,
  required,
  type = 'text',
  hint,
}: {
  label: string
  value: string
  onChange: (value: string) => void
  required?: boolean
  type?: string
  hint?: string
}) {
  return (
    <label className="mdet__field">
      <span>
        {label}
        {required && <b aria-hidden="true"> *</b>}
      </span>
      <input type={type} value={value} required={required} onChange={(e) => onChange(e.target.value)} />
      {hint && <small>{hint}</small>}
    </label>
  )
}
